import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { PhoneInput } from '@travellegacy/nexops-design-system';
import { VerificationModal } from '../components/VerificationModal';
import { paymentSchema } from '@/shared/lib/validations';
import { verificationAPI, customPaymentLinkAPI } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { removePhoneMask, formatCurrency, formatDate } from '@/shared/lib/utils';
import { Loader2, CreditCard, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import {
  PaymentType,
  type PaymentFormData,
  type SendVerificationCodeResponse,
  type CreatePaymentPreferenceResponse,
  type ValidateCustomPaymentTokenResponse,
} from '@/shared/types';

export default function CustomPayment() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationData, setVerificationData] = useState<{
    phone: string;
    expiresAt: string;
  } | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const [tokenData, setTokenData] = useState<ValidateCustomPaymentTokenResponse['data'] | null>(
    null
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    getValues,
  } = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
    },
  });

  // Query para validar token
  const {
    data: tokenValidation,
    isLoading: isValidatingToken,
    error: tokenError,
  } = useQuery({
    queryKey: ['custom-payment-token', token],
    queryFn: async () => {
      if (!token) throw new Error('Token não fornecido');
      const response = await customPaymentLinkAPI.validate(token);
      return response.data.data as ValidateCustomPaymentTokenResponse;
    },
    enabled: !!token,
    retry: false,
  });

  // Atualizar tokenData quando a validação for bem-sucedida
  useEffect(() => {
    if (tokenValidation?.valid && tokenValidation.data) {
      setTokenData(tokenValidation.data);
    }
  }, [tokenValidation]);

  // Mutation para enviar código de verificação
  const sendCodeMutation = useMutation({
    mutationFn: async (phone: string) => {
      const response = await verificationAPI.sendCode({
        phone: removePhoneMask(phone),
      });
      return response.data.data as SendVerificationCodeResponse;
    },
    onSuccess: (data) => {
      const values = getValues();
      setVerificationData({
        phone: values.phone,
        expiresAt: data.expiresAt,
      });
      setShowVerificationModal(true);
      toast.success('Código enviado via WhatsApp!');
    },
    onError: () => {
      toast.error('Erro ao enviar código de verificação. Tente novamente.');
    },
  });

  // Mutation para criar preferência de pagamento com token customizado
  const createPreferenceMutation = useMutation({
    mutationFn: async () => {
      if (!token) {
        throw new Error('Token não encontrado');
      }

      const values = getValues();

      const response = await customPaymentLinkAPI.createPreference({
        token,
        name: values.name,
        email: values.email,
        phone: removePhoneMask(values.phone),
      });
      return response.data.data as CreatePaymentPreferenceResponse;
    },
    onSuccess: (data) => {
      toast.success('Redirecionando para pagamento...');
      localStorage.setItem('mercadopago_init_point', data.initPoint);
      window.location.href = data.initPoint;
    },
    onError: (error: unknown) => {
      const errorMessage =
        (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message || 'Erro ao criar preferência de pagamento.';
      toast.error(errorMessage);
      setIsVerified(false);
    },
  });

  const onSubmit = (data: PaymentFormData) => {
    sendCodeMutation.mutate(data.phone);
  };

  const handleVerified = () => {
    setIsVerified(true);
    toast.success('Telefone verificado com sucesso!');
    createPreferenceMutation.mutate();
  };

  // Loading state
  if (isValidatingToken) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] dark:bg-[#0f0606] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-12">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-12 w-12 text-primary animate-spin" />
              <p className="text-center text-gray-600 dark:text-gray-400">
                Validando link de pagamento...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state - Invalid token
  if (tokenError || !tokenValidation?.valid) {
    const errorMessage = tokenValidation?.message || 'Link de pagamento inválido ou expirado';
    return (
      <div className="min-h-screen bg-[#FAF7F2] dark:bg-[#0f0606] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <CardTitle className="text-red-600 dark:text-red-400">Link Inválido</CardTitle>
            </div>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/payment')} variant="outline" className="w-full">
              Voltar para pagamento padrão
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Se chegou aqui, o token é válido e temos os dados
  const amount = tokenData?.amount || 0;
  const paymentType = tokenData?.paymentType || PaymentType.SINGLE;
  const installments = tokenData?.installments || 1;
  const description = tokenData?.description;
  const expiresAt = tokenData?.expiresAt;

  // Formatar valor em reais e data de expiração usando utilities
  const formattedAmount = formatCurrency(amount);
  const formattedExpiresAt = expiresAt ? formatDate(expiresAt) : null;

  return (
    <div className="min-h-screen bg-[#FAF7F2] dark:bg-[#0f0606] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <CreditCard className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>Pagamento</CardTitle>
          </div>
          <CardDescription>Preencha seus dados para prosseguir com o pagamento</CardDescription>
        </CardHeader>

        <CardContent>
          {/* Payment Info Card */}
          <div className="mb-6 p-4 bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-lg border-2 border-primary/20">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Valor do Pagamento
                </div>
                <div className="text-3xl font-bold text-primary">{formattedAmount}</div>
              </div>
              <div className="p-2 bg-primary/20 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-primary" />
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700 dark:text-gray-300">Modalidade:</span>
                <span className="text-gray-600 dark:text-gray-400">
                  {paymentType === PaymentType.SINGLE
                    ? 'À Vista'
                    : `Parcelado em até ${installments}x`}
                </span>
              </div>

              {description && (
                <div className="flex items-start gap-2 pt-2 border-t border-primary/20">
                  <AlertTriangle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600 dark:text-gray-400">{description}</span>
                </div>
              )}

              {formattedExpiresAt && (
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500 pt-1">
                  <span>Link válido até: {formattedExpiresAt}</span>
                </div>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Input
              label="Nome Completo"
              type="text"
              placeholder="João da Silva"
              error={errors.name?.message}
              {...register('name')}
              disabled={isVerified}
            />

            <Input
              label="E-mail"
              type="email"
              placeholder="seu@email.com"
              error={errors.email?.message}
              {...register('email')}
              disabled={isVerified}
            />

            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  label="Número de Whatsapp"
                  error={errors.phone?.message}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isVerified}
                />
              )}
            />

            {isVerified && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  {createPreferenceMutation.isPending ? (
                    <Loader2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-green-900 dark:text-green-100">
                      {createPreferenceMutation.isPending
                        ? 'Preparando pagamento...'
                        : 'Telefone verificado com sucesso'}
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      {createPreferenceMutation.isPending
                        ? 'Aguarde enquanto redirecionamos você'
                        : 'Você pode prosseguir com o pagamento'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="pt-4">
              <Button
                type="submit"
                className="w-full"
                disabled={sendCodeMutation.isPending || isVerified}
              >
                {sendCodeMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando código...
                  </>
                ) : isVerified ? (
                  'Prosseguir com Pagamento'
                ) : (
                  'Avançar'
                )}
              </Button>
            </div>

            {!isVerified && (
              <p className="text-xs text-center text-gray-600 dark:text-gray-400">
                Seu WhatsApp será usado para enviar confirmações e informações importantes sobre o
                serviço
              </p>
            )}
          </form>
        </CardContent>
      </Card>

      {verificationData && (
        <VerificationModal
          isOpen={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          phone={verificationData.phone}
          expiresAt={verificationData.expiresAt}
          onVerified={handleVerified}
        />
      )}
    </div>
  );
}
